import React from "react";
import { useParams, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { allCinemaVenues } from "./../graphql/queries/benefitQueries";
import {
  enableVenuesMutation,
  disableVenuesMutation,
} from "../graphql/mutations/benefitMutations";
import Permissions from "../configs/permissions";
import usePermissions from "../hooks/permissions";
import useBaseNavigation from "./../hooks/baseNavigation";
import { CINEMA_VENUE_MODEL_NAME } from "./../configs/constants";
import PageSpinner from "./../mood-ui/PageSpinner";
import {
  PageContainer,
  BlockContainer,
  Row,
  Col,
  Centrify,
} from "./../mood-ui/Layout";
import {
  ListItemCheckbox,
  PageControls,
  TEditButton,
} from "./../mood-ui/Controls";
import { ColorLabel, Heading } from "./../mood-ui/Typography";
import Search from "./../mood-ui/Search";
import { ItemsNotFound } from "./../mood-ui/Typography";
import {
  TableContainer,
  TableView,
  THeading,
  TRow,
  TCol,
  TContent,
  THCol,
  Pagination,
} from "./../mood-ui/DataTable";
import useListSelection from "../hooks/rowSelection";
import { TableSelectedActions } from "../moose-ui/DataTable";
import ChainListActions from "../components/list-secondary-actions/benefits/cs/ChainListActions";
import useModal from "../hooks/modal";
import useNotification from "../hooks/notifications";
import { ConfirmActionModal } from "../mood-ui/Modals";
import {
  ErrorNotification,
  SuccessNotification,
} from "../mood-ui/Notifications";

const ALL_CINEMA_VENUES = gql(allCinemaVenues);
const ENABLE_VENUES = gql(enableVenuesMutation);
const DISABLE_VENUES = gql(disableVenuesMutation);

const CinemaVenueList = ({ entityDetails: { offers, cards, merchants } }) => {
  const { protectPage, hasAccess } = usePermissions();

  protectPage(Permissions.Mutation.ManageCinemaSocietyData);

  const {
    selectedRows,
    isRowSelected: rowSelected,
    toggleRow: toggleRowSelection,
    isAllRowsSelected,
    toggleAllRows,
    clearSelection,
  } = useListSelection();

  const { page, limit, orderBy, orderHow, search } = useParams();

  const { search: queryString } = useLocation();

  const { modalState, showModal, closeModal } = useModal();

  const {
    notify: displayNotification,
    notificationState,
    handleClose,
  } = useNotification();

  const {
    notify: displayErrorNotification,
    notificationState: errorNotificationState,
    handleClose: handleCloseError,
  } = useNotification();

  const { data, loading, error, refetch } = useQuery(ALL_CINEMA_VENUES, {
    variables: {
      page: parseInt(page),
      limit: parseInt(limit),
      orderBy,
      orderHow,
      search,
      queryString,
    },
    fetchPolicy: "cache-and-network",
  });

  const { nextPage, prevPage, editForm, applySearch } = useBaseNavigation(
    CINEMA_VENUE_MODEL_NAME
  );

  const [enableVenues, { loading: enableVenuesInProgress }] = useMutation(
    ENABLE_VENUES,
    {
      onCompleted: (_) => {
        refetch();
        displayNotification({
          heading: "Venues Enabled",
          message: "Successfully enabled (activated) selected venues",
          secondsToDisplay: 1,
        });
        clearSelection();
      },
      onError: (e) => {
        displayErrorNotification({
          heading: "Error",
          message: "Failed to enable (activate) selected venues",
          secondsToDisplay: 2,
        });
      },
    }
  );

  const [disableVenues, { loading: disableVenuesInProgress }] = useMutation(
    DISABLE_VENUES,
    {
      onCompleted: (_) => {
        refetch();
        displayNotification({
          heading: "Venues Disabled",
          message: "Successfully disabled (deactivated) selected venues",
          secondsToDisplay: 1,
        });
        clearSelection();
      },
      onError: (e) => {
        displayErrorNotification({
          heading: "Error",
          message: "Failed to disable (deactivate) selected venues",
          secondsToDisplay: 2,
        });
      },
    }
  );

  const confirmEnable = () => {
    showModal({
      heading: "Confirm Enable (Activation)",
      message: "Are you sure you want to Enable/Activate selected venues?",
      mood: "Success",
      confirmButtonLabel: "Enable",
      onConfirm: () => {
        closeModal();
        handleEnableVenues();
      },
      onCancel: () => {
        closeModal();
      },
    });
  };

  const handleEnableVenues = () => {
    enableVenues({ variables: { ids: selectedRows } });
  };

  const confirmDisable = () => {
    showModal({
      heading: "Confirm Disable (Deactivation)",
      message: "Are you sure you want to Disable/Deactivate selected venues?",
      mood: "Warning",
      confirmButtonLabel: "Disable",
      onConfirm: () => {
        closeModal();
        handleDisableVenues();
      },
      onCancel: () => {
        closeModal();
      },
    });
  };

  const handleDisableVenues = () => {
    disableVenues({ variables: { ids: selectedRows } });
  };

  const showUpdateInProgress =
    disableVenuesInProgress || enableVenuesInProgress;

  if (error) return <div>Error</div>;

  return (
    <PageContainer>
      <BlockContainer raised>
        <Row tweaks="border-b border-[#dfe2e8] pb-5 mx-5">
          <Col width={12}>
            <Heading
              inset={true}
              text="Cinema Venues"
              description="Control Cinema Society Venues."
            />
          </Col>
        </Row>
        <Row tweaks="pt-5 pb-4 mt-0 px-5">
          <Col width={9}>
            {/* {hasAccess(Permissions.Mutation.CreateSection) && (
              <AddButton
                label="Refresh list of chains from provider - Cinema Society API"
                loading={loading}
              />
            )} */}
          </Col>
          <Col width={3}>
            <Search onApplySearch={applySearch} />
          </Col>
        </Row>
        <TableContainer tweaks="px-5 pb-5">
          <TableView>
            <THeading>
              <TRow>
                <THCol tweaks="w-4 text-center">
                  <ListItemCheckbox
                    onChange={() =>
                      toggleAllRows(data?.cinemaVenues?.map((v) => v.id))
                    }
                    isChecked={
                      isAllRowsSelected(data?.cinemaVenues?.map((v) => v.id))
                        ? true
                        : false
                    }
                  />
                </THCol>
                <THCol tweaks="text-left" name="id" value="ID" />
                <THCol tweaks="text-left" name="chain_name" value="Chain" />
                <THCol tweaks="text-left" name="name" value="Venue" />
                <THCol tweaks="text-left" name="postcode" value="Postcode" />
                <THCol tweaks="text-left" name="city" value="City" />
                <THCol tweaks="text-left" name="status" value="Status" />
                <THCol tweaks="w-16 text-center" />
              </TRow>
            </THeading>
            {!data && (
              <tbody className="w-full">
                <tr>
                  <td colSpan="100%">
                    <PageSpinner inline />
                  </td>
                </tr>
              </tbody>
            )}
            {data && data.cinemaVenues && data.cinemaVenues.length === 0 && (
              <tbody className="w-full">
                <tr>
                  <td colSpan="100%">
                    <ItemsNotFound text="No Cinema venues found" tweaks="" />
                  </td>
                </tr>
              </tbody>
            )}
            {data && data.cinemaVenues && data.cinemaVenues.length > 0 && (
              <TContent>
                {data.cinemaVenues.map((v, rowIndex) => (
                  <TRow key={v.id}>
                    <TCol>
                      <ListItemCheckbox
                        onChange={() => toggleRowSelection(v.id)}
                        isChecked={rowSelected(v.id) ? true : false}
                      />
                    </TCol>
                    <TCol value={v.id} />
                    <TCol tweaks="max-w-[100px]">
                      <abbr title={v.chain_name}>{v.chain_name}</abbr>
                    </TCol>
                    <TCol tweaks="max-w-[100px]">
                      <abbr title={v.name}>{v.name}</abbr>
                    </TCol>
                    <TCol tweaks="max-w-[100px]">
                      <abbr title={v.postcode}>{v.postcode}</abbr>
                    </TCol>
                    <TCol tweaks="max-w-[100px]">
                      <abbr title={v.city}>{v.city}</abbr>
                    </TCol>
                    <TCol tweaks="max-w-[180px]">
                      {v.status == "Active" && (
                        <ColorLabel text="Active" color="emerald" />
                      )}
                      {v.status == "Disabled" && (
                        <ColorLabel text="Disabled" color="yellow" />
                      )}
                      {v.status == "Draft" && (
                        <ColorLabel text="Draft" color="gray" />
                      )}
                    </TCol>
                    <TCol tweaks="w-16 text-center">
                      {hasAccess(Permissions.Query.Section) && (
                        <TEditButton onClick={() => editForm(v.id)} />
                      )}
                    </TCol>
                  </TRow>
                ))}
              </TContent>
            )}
          </TableView>
          <PageControls spaced>
            <Row tweaks="w-full pl-5">
              <Col width={1}>
                <TableSelectedActions>
                  {selectedRows && selectedRows.length > 0 && (
                    <ChainListActions
                      onClearSelection={clearSelection}
                      onEnable={confirmEnable}
                      onDisable={confirmDisable}
                    />
                  )}
                </TableSelectedActions>
              </Col>
              <Col width={8} extraStyles="">
                {showUpdateInProgress && (
                  <Centrify>
                    <ColorLabel text="Appying changes..." color="blue" />
                  </Centrify>
                )}
              </Col>
              <Col width={3}>
                <Pagination
                  onPrev={page > 1 ? prevPage : false}
                  onNext={
                    data?.cinemaVenues?.length >= limit - 1 ? nextPage : false
                  }
                />
              </Col>
            </Row>
          </PageControls>
        </TableContainer>
      </BlockContainer>
      <ConfirmActionModal {...modalState} />
      <SuccessNotification {...notificationState} onClose={handleClose} />
      <ErrorNotification
        {...errorNotificationState}
        onClose={handleCloseError}
      />
    </PageContainer>
  );
};

export default CinemaVenueList;
